/* eslint-disable max-lines */
import React, { useMemo } from 'react';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import { ia } from 'lib/helpers/utility';

import AGTable from '../AGTable/AGTable';
import CustomStatusBarAggregationComponent from '../AGTable/CustomStatusBarAggregationComponent';
import { CustomStatusBarCount } from '../AGTable/CustomStatusBarCount';
import { CustomStatusBarPagination } from '../AGTable/CustomStatusBarPagination';

import { RowSelectionActions } from './components/RowSelectionActions';
import TableFilters from './components/TableFilters';
import TableHeader from './components/TableHeader';
import {
  DEFAULT_COLUMN_DEFS,
  DEFAULT_PAGE_COUNT,
  DEFAULT_STALE_TIME,
  GRID_OPTIONS
} from './lib/constants';
import { createQueryFilters } from './lib/createQueryFilters';

const AgTablePage = (props) => {
  const {
    defaultFilters = {},
    defaultColumns,
    name,
    pagination = true,
    getExportingData,
    exportParams,
    ...rest
  } = props;

  return (
    <TableContextProvider
      defaultFilters={defaultFilters}
      cols={defaultColumns}
      name={name}
      pagination={pagination}
      getData={getExportingData}
      exportParams={exportParams}
      {...rest}>
      <AgTablePageContent {...rest} />
    </TableContextProvider>
  );
};

const AgTablePageContent = ({
  useData,
  queryParams = {},
  queryOptions = {},
  ModalComponent,
  subtitle,
  onCellClicked,
  onSelectRow,
  headerActions,
  refresh = false,
  skeletonSize,
  noTitle,
  switchItems,
  title,
  rightHeaderActions,
  noNewButton,
  actions,
  noHeader,
  formatData,
  queryKey,
  newButtonText,
  disabledCells = [],
  type,
  dependencies,
  noDisplayButton = false,
  CustomPagination,
  CustomCount,
  ...rest
}) => {
  const {
    name,
    limit,
    pagination,
    filters,
    changeFilters,
    defaultFilters,
    initialFilters,
    errors,
    forceRefresh,
    isEditingFilter,
    selectedItem,
    setSelectedItem,
    showModal,
    setShowModal,
    filtersInitialized,
    onPageChange,
    sortField,
    sort,
    view,
    page
  } = useTableContext();

  const isSwitchItemView = useMemo(() => {
    return switchItems?.some((item) => item.value === view);
  }, [switchItems, view]);

  const queryFilters = useMemo(() => {
    if (!filtersInitialized) return {};
    const result = createQueryFilters(filters, isSwitchItemView && view);
    return result;
  }, [filters, view, isSwitchItemView, filtersInitialized]);

  const newDependencies = useMemo(() => {
    const baseDeps = [limit, page, sort, queryFilters, view, filtersInitialized, sortField];
    if (ia(dependencies)) {
      return [...baseDeps, ...dependencies];
    }
    return [...baseDeps, ...Object.values(queryParams)];
  }, [
    limit,
    page,
    sort,
    queryFilters,
    queryParams,
    view,
    filtersInitialized,
    dependencies,
    sortField
  ]);

  const { data, refetch, isLoading, isRefetching } = useData({
    name,
    params: {
      limit,
      page,
      ...(sortField && { sort: sortField }),
      filters: queryFilters,
      withCount: true,
      ...queryParams
    },
    options: {
      staleTime: DEFAULT_STALE_TIME,
      enabled: filtersInitialized,
      ...queryOptions
    },
    dependencies: newDependencies
  });

  const loading = !filtersInitialized || isLoading || isRefetching;

  const items = useMemo(() => {
    if (loading) return;
    return formatData ? formatData(data || []) : data?.[queryKey || name] || [];
  }, [data, name, loading, formatData, queryKey]);

  const count = Number(data?.count) ?? DEFAULT_PAGE_COUNT;
  const statusPanelData = data?.statusPanelData;

  const onCellClickedHandler = (event) => {
    if (loading) return;

    if (['actions', '0', ...disabledCells].includes(event?.column?.colId) || event?.node?.group)
      return;

    if (typeof onCellClicked === 'function') {
      onCellClicked(event);
      return;
    }

    if (typeof onSelectRow === 'function') {
      onSelectRow(event?.data, event);
      return;
    }
    setSelectedItem(event?.data);
    setShowModal(true);
  };

  const onModalToggle = () => {
    setShowModal((prev) => !prev);
    setSelectedItem(null);
  };

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: CustomCount ? CustomCount : CustomStatusBarCount,
          statusPanelParams: CustomCount ? { data } : { data: items, count, pagination },
          align: 'left'
        },
        ...(pagination
          ? [
              {
                statusPanel: CustomPagination ? CustomPagination : CustomStatusBarPagination,
                statusPanelParams: CustomPagination ? { data } : { data: items, count },
                align: 'right'
              }
            ]
          : []),
        ...(statusPanelData
          ? [
              {
                statusPanel: CustomStatusBarAggregationComponent,
                statusPanelParams: {
                  statusPanelData
                },
                align: 'center'
              }
            ]
          : [])
      ]
    };
  }, [items, count, page, limit, onPageChange, statusPanelData, pagination]);

  return (
    <>
      {!noHeader && (
        <TableHeader
          category={name}
          onModalToggle={onModalToggle}
          hasNew={!!ModalComponent && !noNewButton}
          subtitle={subtitle}
          actions={headerActions}
          refresh={refresh}
          onRefresh={refetch}
          noTitle={noTitle}
          switchItems={switchItems}
          title={title}
          count={count}
          rightActions={rightHeaderActions}
          loading={loading}
          newButtonText={newButtonText}
          statusPanelData={statusPanelData}
        />
      )}

      <TableFilters
        filters={filters}
        initialFilters={initialFilters}
        defaultFilters={defaultFilters}
        changeFilters={changeFilters}
        category={name}
        isEditingFilter={isEditingFilter}
        items={switchItems}
        type={type}
        noDisplayButton={noDisplayButton}
      />
      <div className="relative flex h-full flex-col overflow-hidden">
        <div className="ag-theme-quartz h-full">
          <AGTable
            key={forceRefresh}
            data={items}
            gridOptions={GRID_OPTIONS}
            defaultColDef={DEFAULT_COLUMN_DEFS}
            onCellClicked={onCellClickedHandler}
            statusBar={statusBar}
            loading={loading}
            skeletonSize={skeletonSize}
            count={count}
            {...rest}
          />
        </div>
        <RowSelectionActions actions={actions} loading={loading} />
      </div>
      {ModalComponent && (
        <ModalComponent
          item={selectedItem}
          isOpen={showModal}
          handleClose={onModalToggle}
          filters={filters}
          errors={errors}
          category={name}
          tableData={items}
        />
      )}
    </>
  );
};

export default AgTablePage;
